/**
 * Niveaus React Query Hooks
 *
 * Custom hooks for niveau management operations.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { niveausApi, UpdateNiveauInput } from '../../lib/niveausApi';

// Query keys for niveaus
export const niveauKeys = {
  all: ['niveaus'] as const,
  lists: () => [...niveauKeys.all, 'list'] as const,
  detail: (id: number) => [...niveauKeys.all, 'detail', id] as const,
  summary: () => [...niveauKeys.all, 'summary'] as const,
};

/**
 * Hook to fetch all niveaus with statistics
 */
export function useNiveausWithStats() {
  return useQuery({
    queryKey: niveauKeys.lists(),
    queryFn: () => niveausApi.getAll(),
  });
}

/**
 * Hook to fetch a single niveau by ID
 */
export function useNiveau(id: number | undefined) {
  return useQuery({
    queryKey: niveauKeys.detail(id!),
    queryFn: () => niveausApi.getById(id!),
    enabled: !!id,
  });
}

/**
 * Hook to fetch niveau summary
 */
export function useNiveauSummary() {
  return useQuery({
    queryKey: niveauKeys.summary(),
    queryFn: () => niveausApi.getSummary(),
  });
}

/**
 * Hook to update a niveau
 */
export function useUpdateNiveau() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: UpdateNiveauInput }) =>
      niveausApi.update(id, input),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: niveauKeys.all });
      queryClient.invalidateQueries({ queryKey: niveauKeys.detail(id) });
    },
  });
}
